<template>
  <div class="portal-app-page">
    <RetailerHeader />
    <main class="portal-main portal-container">
    <PortalPageHeader eyebrow="Account" title="My orders" description="Review your UCARI wholesale order history and item details." />

    <div v-if="error" class="uk-alert-danger" uk-alert>{{ error }}</div>

    <!-- Empty State -->
    <div v-if="!loading && !orders.length" class="portal-empty-state">
      <div uk-icon="icon: file-text; ratio: 2"></div>
      <h2>No orders yet</h2>
      <p>Your completed partner orders will appear here.</p>
      <router-link :to="{ name: 'Shop' }" class="uk-button uk-button-primary">Browse products</router-link>
    </div>

    <!-- Loading (initial) -->
    <div v-if="loading && !orders.length" class="uk-text-center uk-margin-large">
      <span uk-spinner="ratio: 2"></span>
    </div>

    <!-- Orders List (Accordion) -->
    <ul v-if="orders.length" class="order-list" uk-accordion="multiple: true">
      <li v-for="(o, index) in orders"
        :key="o.id" 
        :class="['order-card', { 'uk-open': index === 0 }]"
      >
        <a class="uk-accordion-title order-card__summary" href="#">
          <div class="uk-grid-small uk-flex-middle" uk-grid>
            <div class="uk-width-expand">
              <div class="uk-text-bold uk-text-truncate" :title="o.id">
                Order #{{ shortId(o.id) }}
              </div>
              <div class="order-card__meta">
                <span>{{ when(o.created_at) }}</span><span>${{ money(o.total_money?.amount) }}</span>
              </div>
            </div>
            <!--
            <div>
              <span :class="['uk-label', statusClass(o.state)]">{{ o.state || '—' }}</span>
            </div>
            -->
          </div>
        </a>

        <div class="uk-accordion-content">
          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-1 uk-width-2-3@m">
              <div class="order-card__label">Items</div>
              <ul class="order-items">
                <li v-for="li in (o.line_items || [])" :key="li.uid">
                  <div class="uk-width-expand uk-text-truncate">
                    <div class="order-item__name">{{ li.name }}</div>
                    <div class="order-item__variation" v-if="li.variation_name">{{ li.variation_name }}</div>
                  </div>
                  <div class="uk-text-right">
                    <div class="uk-text-nowrap">{{ li.quantity }} x ${{ money(li.base_price_money?.amount) }}</div>
                    <div class="uk-text-bold uk-text-nowrap">Total: ${{ money(li.total_money?.amount) }}</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <!-- Pagination -->
    <div class="uk-margin" v-if="nextCursor">
      <button class="uk-button uk-button-default" @click="loadMore" :disabled="loading">
        <span v-if="loading" uk-spinner></span>
        <span v-else>Load More</span>
      </button>
    </div>
    </main>
    <RetailerFooter />
  </div>
</template>

<script>
/* global UIkit */
import axios from 'axios';
import RetailerHeader from './components/RetailerHeader.vue';
import RetailerFooter from './components/RetailerFooter.vue';
import PortalPageHeader from './components/PortalPageHeader.vue';

export default {
  name: 'OrdersView',
  components: { RetailerHeader, RetailerFooter, PortalPageHeader },
  data() {
    return {
      cid: '',
      accessToken: '',
      squareCustomerId: '',
      orders: [],
      nextCursor: null,
      loading: false,
      error: null,
      orderStates: '',
      pageLimit: 50,
      sort: 'DESC',
      cartCount: 0,
    };
  },
  created() {
    document.title = 'Orders | UCARI';
    this.accessToken = localStorage.getItem('accessToken') || '';
    this.cid = localStorage.getItem('cid') || '';
    this.squareCustomerId = localStorage.getItem('squareCustomerId') || '';

    if (!this.accessToken || !this.cid) {
      this.$router.replace({ name: 'LoginUser', query: { redirect: '/orders' } });
      return;
    }
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      this.error = null;
      this.orders = [];
      this.nextCursor = null;

      try {
        const url = `${process.env.VUE_APP_API_URL}/api/orders`;
        const { data } = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            cid: this.cid,
          },
          params: {
            customerId: this.squareCustomerId,
            state: this.orderStates,
            limit: this.pageLimit,
            sort: this.sort,
          },
        });

        this.orders = data?.orders || [];
        this.nextCursor = data?.cursor || null;
      } catch (e) {
        console.error(e);
        this.error = e?.response?.data?.error || e?.message || 'Failed to load orders';
      } finally {
        this.loading = false;
        window.UIkit?.update();
      }
    },

    async loadMore() {
      if (!this.nextCursor || !this.squareCustomerId) return;

      this.loading = true;
      this.error = null;

      try {
        const url = `${process.env.VUE_APP_API_URL}/api/orders`;
        const { data } = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            cid: this.cid,
          },
          params: {
            customerId: this.squareCustomerId,
            state: this.orderStates,
            limit: this.pageLimit,
            sort: this.sort,
            cursor: this.nextCursor,
          },
        });

        this.orders.push(...(data?.orders || []));
        this.nextCursor = data?.cursor || null;
      } catch (e) {
        console.error(e);
        this.error = e?.response?.data?.error || e?.message || 'Failed to load more orders';
      } finally {
        this.loading = false;
        window.UIkit?.update();
      }
    },

    money(cents) {
      if (cents == null) return '—';
      return (Number(cents) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    when(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },

    shortId(id) {
      if (!id) return '—';
      // show the last 8 chars as a friendlier order number; tweak to taste
      return String(id).slice(-8).toUpperCase();
    },

    statusClass(state) {
      switch ((state || '').toUpperCase()) {
        case 'COMPLETED':
          return 'uk-label-success';
        case 'CANCELED':
        case 'CANCELLED':
          return 'uk-label-danger';
        case 'OPEN':
          return 'uk-label-warning';
        default:
          return 'uk-label';
      }
    },

    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        UIkit.notification({ message: 'Copied to clipboard', status: 'success', timeout: 1500 });
      } catch {
        UIkit.notification({ message: 'Copy failed', status: 'danger', timeout: 1500 });
      }
    },

    handleLogout() {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('cid');
      localStorage.removeItem('squareCustomerId');
      this.$router.push('/login');
    },

    // placeholders to avoid errors if header emits them; implement in parent if needed
    openCart() {},
  },
};
</script>
